import { useEffect, useRef } from "react";
import { FORMATE, FORMATNAMEN } from "../hilfen/zahlformat";

/*
  Das Menü an einem Zeilen- oder Spaltenkopf.

  ---------------------------------------------------------------
  Warum ein Menü und kein Ziehen
  ---------------------------------------------------------------

  In Excel schiebt man Spaltengrenzen mit der Maus und zieht Zeilen an ihrem
  Kopf an eine andere Stelle. Beides verlangt Präzision — genau das, was die
  Regel des Projekts ausschließt, und auf einem Tablet mit dem Finger kaum
  zu treffen.

  Deshalb läuft hier alles über große Knöpfe: einfügen, löschen, verschieben,
  Format, Breite. Der Kopf selbst ist der Auslöser und über die Tastatur
  erreichbar; das Menü darunter hat Knöpfe in voller Antippgröße.

  Anders als beim Aufgabenbrett und der Folienbühne gibt es in diesem
  Bereich also KEINE Ausnahme von der Präzisionsregel.
*/

const BREITEN = [
  { wert: "schmal", titel: "Schmal" },
  { wert: "normal", titel: "Normal" },
  { wert: "breit", titel: "Breit" },
];

export default function Rastermenue({
  art,
  stelle,
  name,
  format,
  breite,
  anzahl,
  anker,
  aufEinfuegenDavor,
  aufEinfuegenDanach,
  aufLoeschen,
  aufHoch,
  aufRunter,
  aufFormat,
  aufBreite,
  aufSchliessen,
}) {
  const feldRef = useRef(null);

  /* Escape schließt — wie bei jedem anderen Kasten, der sich überlegt. */
  useEffect(() => {
    const beiTaste = (e) => {
      if (e.key === "Escape") aufSchliessen();
    };
    window.addEventListener("keydown", beiTaste);
    if (feldRef.current) feldRef.current.focus();
    return () => window.removeEventListener("keydown", beiTaste);
  }, [aufSchliessen]);

  const istSpalte = art === "spalte";
  const wort = istSpalte ? "Spalte" : "Zeile";

  return (
    <>
      <button className="zziel__hinterfeld" aria-label="Menü schließen" onClick={aufSchliessen} />

      <div
        className="rastermenue"
        style={platziere(anker)}
        role="dialog"
        aria-label={`${wort} ${name}`}
        ref={feldRef}
        tabIndex={-1}
      >
        <p className="rastermenue__kopf">
          {wort} {name}
        </p>

        <div className="rastermenue__gruppe">
          <button className="knopf knopf--schlicht" onClick={aufEinfuegenDavor}>
            {istSpalte ? "Spalte links einfügen" : "Zeile darüber einfügen"}
          </button>
          <button className="knopf knopf--schlicht" onClick={aufEinfuegenDanach}>
            {istSpalte ? "Spalte rechts einfügen" : "Zeile darunter einfügen"}
          </button>
        </div>

        <div className="rastermenue__gruppe">
          <button className="knopf knopf--schlicht" onClick={aufHoch} disabled={stelle === 0}>
            {istSpalte ? "Nach links schieben" : "Nach oben schieben"}
          </button>
          <button
            className="knopf knopf--schlicht"
            onClick={aufRunter}
            disabled={stelle >= anzahl - 1}
          >
            {istSpalte ? "Nach rechts schieben" : "Nach unten schieben"}
          </button>
        </div>

        {istSpalte && (
          <>
            <div className="rastermenue__gruppe">
              <p className="rastermenue__was">Format</p>
              <div className="rastermenue__reihe">
                {FORMATE.map((f) => (
                  <button
                    key={f}
                    className={`knopf ${f === format ? "" : "knopf--schlicht"}`}
                    onClick={() => aufFormat(f)}
                    aria-pressed={f === format}
                  >
                    {FORMATNAMEN[f]}
                  </button>
                ))}
              </div>
            </div>

            <div className="rastermenue__gruppe">
              <p className="rastermenue__was">Breite</p>
              <div className="rastermenue__reihe">
                {BREITEN.map((b) => (
                  <button
                    key={b.wert}
                    className={`knopf ${b.wert === breite ? "" : "knopf--schlicht"}`}
                    onClick={() => aufBreite(b.wert)}
                    aria-pressed={b.wert === breite}
                  >
                    {b.titel}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="rastermenue__gruppe">
          {/*
            Löschen steht unten und allein. Es ist der einzige Punkt, der
            etwas wegnimmt — und die letzte Zeile beziehungsweise Spalte
            lässt sich gar nicht löschen, sonst käme man nicht wieder ins
            Blatt hinein.
          */}
          <button
            className="knopf knopf--schlicht"
            onClick={aufLoeschen}
            disabled={anzahl <= 1}
            style={{ color: anzahl <= 1 ? undefined : "var(--ampel-nein)" }}
          >
            {wort} {name} löschen
          </button>
        </div>
      </div>
    </>
  );
}

/*
  Unter dem Kopf, an dem es hängt — und nie über den Bildschirmrand hinaus.
  Ist unten kein Platz, klappt es nach oben.
*/
function platziere(anker) {
  if (!anker) return { left: "50%", top: "20%", transform: "translateX(-50%)" };

  const BREITE = 260;
  const RAND = 12;

  let links = anker.links;
  if (links + BREITE > window.innerWidth - RAND) links = window.innerWidth - BREITE - RAND;
  if (links < RAND) links = RAND;

  const platzUnten = window.innerHeight - anker.unten;
  return platzUnten > 360
    ? { left: `${links}px`, top: `${anker.unten + 6}px` }
    : { left: `${links}px`, bottom: `${window.innerHeight - anker.oben + 6}px` };
}
